import React from "react";
import { LockKey, Plus, Trash } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";

const lockedLabel = (locked) =>
  locked ? (
    <LockKey className="ml-1 inline h-3.5 w-3.5 align-[-2px] text-amber-400" />
  ) : null;

const disabledClass = (disabled) => (disabled ? "opacity-60" : "");

export function InvoiceStatusFields({ formData, setFormData, t }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        <Label className="text-[#9CA3AF]">{t("status")}</Label>
        <Select
          value={formData.status}
          onValueChange={(v) => setFormData({ ...formData, status: v })}
        >
          <SelectTrigger className="valhalla-input">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#151B26] border-[#242E42]">
            <SelectItem value="borrador">{t("draft")}</SelectItem>
            <SelectItem value="emitida">{t("issued")}</SelectItem>
            <SelectItem value="pagada">{t("paid")}</SelectItem>
            <SelectItem value="vencida">{t("overdue")}</SelectItem>
            <SelectItem value="cancelada">{t("cancelled")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-[#9CA3AF]">Paid Date</Label>
        <Input
          type="date"
          value={formData.paid_date}
          onChange={(e) => setFormData({ ...formData, paid_date: e.target.value })}
          className="valhalla-input"
        />
      </div>
    </div>
  );
}

export function InvoiceEntityFields({
  formData,
  setFormData,
  clients,
  projects,
  t,
  disabled = false,
  showLocks = false,
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        <Label className="text-[#9CA3AF]">
          {t("client")}
          {lockedLabel(showLocks && disabled)}
        </Label>
        <Select
          value={formData.client_id}
          onValueChange={(v) => setFormData({ ...formData, client_id: v })}
          disabled={disabled}
        >
          <SelectTrigger className={`valhalla-input ${disabledClass(disabled)}`}>
            <SelectValue placeholder="Select client" />
          </SelectTrigger>
          <SelectContent className="bg-[#151B26] border-[#242E42]">
            {clients.map((client) => (
              <SelectItem key={client._id} value={client._id}>
                {client.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-[#9CA3AF]">
          Project (optional)
          {lockedLabel(showLocks && disabled)}
        </Label>
        <Select
          value={formData.project_id || "none"}
          onValueChange={(v) =>
            setFormData({ ...formData, project_id: v === "none" ? "" : v })
          }
          disabled={disabled}
        >
          <SelectTrigger className={`valhalla-input ${disabledClass(disabled)}`}>
            <SelectValue placeholder="Select project" />
          </SelectTrigger>
          <SelectContent className="bg-[#151B26] border-[#242E42]">
            <SelectItem value="none">No project</SelectItem>
            {projects.map((project) => (
              <SelectItem key={project._id} value={project._id}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function InvoiceDateFields({
  formData,
  setFormData,
  t,
  disabled = false,
  showLocks = false,
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        <Label className="text-[#9CA3AF]">
          {t("issueDate")}
          {lockedLabel(showLocks && disabled)}
        </Label>
        <Input
          type="date"
          value={formData.issue_date}
          onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
          className={`valhalla-input ${disabledClass(disabled)}`}
          readOnly={disabled}
          disabled={disabled}
          required
        />
      </div>

      <div className="space-y-2">
        <Label className="text-[#9CA3AF]">
          {t("dueDate")}
          {lockedLabel(showLocks && disabled)}
        </Label>
        <Input
          type="date"
          value={formData.due_date}
          onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
          className={`valhalla-input ${disabledClass(disabled)}`}
          readOnly={disabled}
          disabled={disabled}
          required
        />
      </div>
    </div>
  );
}

export function InvoiceItemsEditor({
  formData,
  t,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onTaxRateChange,
  disabled = false,
  showLocks = false,
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Label className="text-[#9CA3AF]">
          Items
          {lockedLabel(showLocks && disabled)}
        </Label>
        {!disabled ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onAddItem}
            className="border-[#242E42]"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Item
          </Button>
        ) : null}
      </div>

      {formData.items.map((item, index) => (
        <div
          key={index}
          className="grid grid-cols-2 gap-2 sm:grid-cols-12 sm:items-end"
        >
          <div className="col-span-2 sm:col-span-5">
            <Input
              placeholder="Description"
              value={item.description}
              onChange={(e) => onUpdateItem(index, "description", e.target.value)}
              className={`valhalla-input ${disabledClass(disabled)}`}
              readOnly={disabled}
              disabled={disabled}
            />
          </div>
          <div className="sm:col-span-2">
            <Input
              type="number"
              placeholder="Qty"
              value={item.quantity}
              onChange={(e) =>
                onUpdateItem(index, "quantity", parseFloat(e.target.value) || 0)
              }
              className={`valhalla-input ${disabledClass(disabled)}`}
              readOnly={disabled}
              disabled={disabled}
            />
          </div>
          <div className="sm:col-span-2">
            <Input
              type="number"
              placeholder="Price"
              value={item.unit_price}
              onChange={(e) =>
                onUpdateItem(index, "unit_price", parseFloat(e.target.value) || 0)
              }
              className={`valhalla-input ${disabledClass(disabled)}`}
              readOnly={disabled}
              disabled={disabled}
            />
          </div>
          <div className="sm:col-span-2">
            <Input
              type="number"
              value={item.total}
              readOnly
              className="valhalla-input bg-[#0B0E14]"
            />
          </div>
          <div className="flex justify-end sm:col-span-1">
            {!disabled && formData.items.length > 1 ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => onRemoveItem(index)}
                className="text-red-400"
              >
                <Trash className="w-4 h-4" />
              </Button>
            ) : null}
          </div>
        </div>
      ))}

      <div className="flex justify-end">
        <div className="w-full space-y-2 rounded-lg border border-[#242E42] bg-[#0B0E14] p-4 sm:w-72">
          <div className="flex justify-between text-sm">
            <span className="text-[#9CA3AF]">{t("subtotal")}</span>
            <span className="text-[#F3F4F6]">${formData.subtotal.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#9CA3AF]">
              {t("taxRate")} (%)
              {lockedLabel(showLocks && disabled)}
            </span>
            <Input
              type="number"
              value={formData.tax_rate}
              onChange={(e) => onTaxRateChange(parseFloat(e.target.value) || 0)}
              className={`valhalla-input w-20 text-right ${disabledClass(disabled)}`}
              readOnly={disabled}
              disabled={disabled}
            />
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[#9CA3AF]">{t("taxAmount")}</span>
            <span className="text-[#F3F4F6]">${formData.tax_amount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between border-t border-[#242E42] pt-2">
            <span className="font-semibold text-[#F3F4F6]">{t("total")}</span>
            <span className="font-['Outfit'] text-xl font-bold text-[#E2B05E]">
              ${formData.total.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function InvoiceNotesField({ formData, setFormData, t }) {
  return (
    <div className="space-y-2">
      <Label className="text-[#9CA3AF]">{t("notes")}</Label>
      <Textarea
        value={formData.notes}
        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        className="valhalla-input"
      />
    </div>
  );
}
